import { useEffect, useMemo, useState, type ReactElement } from "react";
import { AppBar, Logout, TitlePortal, UserMenu, useGetIdentity, usePermissions } from "react-admin";
import type { AppBarProps } from "react-admin";
import HomeIcon from "@mui/icons-material/Home";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  MenuItem,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useBasename } from "ra-core";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged, type User } from "firebase/auth";

import {
  personalDashboardNavigation,
  shouldShowPersonalDashboardUserMenuItem,
} from "@/containers/staff/dashboard/staff-dashboard-helpers";
import { authObj } from "@/firebase-config";
import type { PermissionRecord } from "@/types/permissions";
import { hasPermission } from "@/utils/permission-utils";
import { clearStoredTenantBranding } from "@/utils/tenant-branding-cache";
import { navigateToBaseRoute, useBaseRoute } from "@/utils/route-utility";

const LayoutAppBar = (props: AppBarProps): ReactElement => {
  const navigate = useNavigate();
  const basename = useBasename();
  const baseRoute = useBaseRoute();
  const showBackToDashboard = Boolean(baseRoute);
  const [authUser, setAuthUser] = useState<User | null>(authObj.currentUser);
  const { data: identityData } = useGetIdentity();
  const identity = identityData as { id?: string; fullName?: string; email?: string } | undefined;
  const { permissions } = usePermissions<PermissionRecord>();
  const showMyDashboard = shouldShowPersonalDashboardUserMenuItem(
    hasPermission(permissions, "myAttendance.view"),
    hasPermission(permissions, "employees.view")
  );

  useEffect(() => onAuthStateChanged(authObj, setAuthUser), []);

  const initials = useMemo(() => {
    const displayName = identity?.fullName?.trim() || authUser?.displayName?.trim();
    if (displayName) {
      return displayName
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? "")
        .join("");
    }
    const email = authUser?.email?.trim();
    if (!email) return "";
    return email[0]?.toUpperCase() ?? "";
  }, [identity?.fullName, authUser?.displayName, authUser?.email]);

  const avatarNode = (
    <Avatar
      src={authUser?.photoURL ?? undefined}
      alt={authUser?.displayName ?? authUser?.email ?? "Profile"}
      sx={(theme) => ({
        width: 32,
        height: 32,
        bgcolor: "primary.main",
        color: "primary.contrastText",
        border: 2,
        borderColor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.common.white, 0.7)
            : alpha(theme.palette.common.white, 0.3),
      })}
    >
      {initials ? (
        <Typography variant="body1">{initials}</Typography>
      ) : (
        <AccountCircleIcon fontSize="small" />
      )}
    </Avatar>
  );
  const userDisplayName =
    identity?.fullName?.trim() || authUser?.displayName?.trim() || "Signed-in user";
  const userEmail = identity?.email?.trim() || authUser?.email?.trim() || "Email unavailable";
  const isSuperAdmin = localStorage.getItem("isSuperAdmin") === "1";
  const canSwitchTenant = isSuperAdmin;

  /** Match {@link Menu.DashboardItem}: pathname module switch + hash router navigation (pushState alone does not notify HashRouter). */
  const goToDashboard = (): void => {
    navigateToBaseRoute(null);
    navigate(`${basename}/`, { replace: true });
  };

  const handleExitImpersonation = (): void => {
    localStorage.removeItem("impersonatedTenantId");
    clearStoredTenantBranding();
    sessionStorage.setItem("permissionsCacheBust", String(Date.now()));
    // Force a full document reload (not hash-only) so React Query's in-memory
    // permission cache is cleared before landing on the tenant selector page.
    window.location.replace(`${window.location.origin}/?_=${Date.now()}#/select-tenant`);
  };

  return (
    <AppBar
      sx={{
        "& .RaAppBar-menuButton": {
          display: "",
        },
      }}
      elevation={2}
      userMenu={
        <UserMenu icon={avatarNode}>
          <MenuItem
            disableRipple
            disabled
            sx={{ opacity: 1, pointerEvents: "none", maxWidth: 320 }}
          >
            <Box sx={{ py: 0.25 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {userDisplayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {userEmail}
              </Typography>
            </Box>
          </MenuItem>
          {canSwitchTenant && <Divider />}
          {canSwitchTenant && <MenuItem onClick={handleExitImpersonation}>Switch Tenant</MenuItem>}
          <Divider />
          {showMyDashboard && (
            <MenuItem
              onClick={() => {
                const { baseRoute, hashPaths } = personalDashboardNavigation();
                navigateToBaseRoute(baseRoute, null, hashPaths);
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <DashboardCustomizeIcon fontSize="small" />
              </ListItemIcon>
              My dashboard
            </MenuItem>
          )}
          <Logout />
        </UserMenu>
      }
      {...props}
    >
      <TitlePortal />
      {showBackToDashboard && (
        <>
          <IconButton
            color="inherit"
            aria-label="Back to Dashboard"
            onClick={goToDashboard}
            sx={{ display: { xs: "inline-flex", sm: "none" } }}
          >
            <HomeIcon />
          </IconButton>
          <Button
            color="inherit"
            startIcon={<HomeIcon />}
            onClick={goToDashboard}
            size="small"
            sx={{ display: { xs: "none", sm: "inline-flex" } }}
          >
            Back to Home
          </Button>
        </>
      )}
    </AppBar>
  );
};

export default LayoutAppBar;
